/**
 * NodeTypesCacheService.js
 * 
 * Runtime cache for _nodetypes with instant lookup by key or alias.
 */

// Firebase
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { collections } from 'firebaseConfig';

// Config
const LS_KEY_DATA_PREFIX = 'nodetypes-cache.v1';
const LS_KEY_META_PREFIX = 'nodetypes-cache-meta.v1'; // { ts:number, rev:string }
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 min
//const DEFAULT_TTL_MS = 1 * 60 * 1000; // 1 min

const SCOPE_ROOT = 'ROOT';
const SCOPE_NONE = 'NONE';
const SCOPE_WORKSPACE_PREFIX = 'WORKSPACE:';

// Helpers
const upper = (s) => String(s || '').toUpperCase();
const normalizeTypeKey = (value) => String(value || '').trim();
const safeArray = (v) => (Array.isArray(v) ? v : []);
const now = () => Date.now();

/**
 * Normalizes a scope key.
 * 
 * Rules:
 * - null/empty -> ROOT
 * - NONE -> NONE
 * - ROOT -> ROOT
 * - WORKSPACE:<key> -> WORKSPACE:<key>
 * - anything else -> ROOT
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {string} - The normalized scope key.
 */
function normalizeSingleScopeKey(scopeKey) {
  const s = String(scopeKey || '').trim();
  if (!s) return SCOPE_ROOT;
  if (s === SCOPE_NONE) return SCOPE_NONE;
  if (s === SCOPE_ROOT) return SCOPE_ROOT;
  if (s.startsWith(SCOPE_WORKSPACE_PREFIX)) {
    const wk = s.slice(SCOPE_WORKSPACE_PREFIX.length).trim();
    return wk ? `${SCOPE_WORKSPACE_PREFIX}${wk}` : SCOPE_NONE;
  }
  return SCOPE_ROOT;
}

function normalizeScopeKey(scopeKey) {
  const raw = String(scopeKey || '').trim();
  if (!raw) return normalizeSingleScopeKey(raw);
  if (!raw.includes('|')) return normalizeSingleScopeKey(raw);

  const parts = raw
    .split('|')
    .map((p) => normalizeSingleScopeKey(p))
    .filter(Boolean);

  const deduped = [];
  parts.forEach((p) => {
    if (!deduped.includes(p)) deduped.push(p);
  });

  return deduped.join('|') || SCOPE_NONE;
}

function splitScopeKeys(scopeKey) {
  const normalized = normalizeScopeKey(scopeKey);
  if (!normalized) return [];
  if (!normalized.includes('|')) return [normalized];
  return normalized.split('|').map((p) => p.trim()).filter(Boolean);
}

/**
 * Generates a localStorage safe scope key.
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {string} - The localStorage safe scope key.
 */
function lsSafeScope(scopeKey) {
  return normalizeScopeKey(scopeKey).replace(/[^a-zA-Z0-9:_|-]+/g, '_');
}

/**
 * Generates localStorage keys for data and meta.
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {string} - The localStorage key for data.
 */
function lsKeyData(scopeKey) {
  return `${LS_KEY_DATA_PREFIX}.${lsSafeScope(scopeKey)}`;
}

/**
 * Generates localStorage keys for data and meta.
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {string} - The localStorage key for meta.
 */
function lsKeyMeta(scopeKey) {
  return `${LS_KEY_META_PREFIX}.${lsSafeScope(scopeKey)}`;
}

/**
 * Scopes a Firestore collection reference based on the scope key.
 * 
 * @param {object} db - Firestore database instance.
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {object|null} - The Firestore collection reference, or null for NONE scope.
 */
function scopeToDatabase(db, scopeKey) {
  const s = normalizeScopeKey(scopeKey);
  if (s === SCOPE_NONE) {
    return null;
  }
  if (s === SCOPE_ROOT) {
    return collection(db, collections.nodetypes);
  }
  if (s.startsWith(SCOPE_WORKSPACE_PREFIX)) {
    const workspaceKey = s.slice(SCOPE_WORKSPACE_PREFIX.length);
    return collection(db, collections.workspaces, workspaceKey, collections.nodetypes);
  }
  return collection(db, collections.nodetypes);
}

function mergeDocsByScope(scopeOrder, docsByScope) {
  const merged = new Map();
  scopeOrder.forEach((scope) => {
    const docs = docsByScope.get(scope) || [];
    docs.forEach((docData) => {
      const key = normalizeTypeKey(docData?.key || '');
      if (!key || merged.has(key)) return;
      merged.set(key, docData);
    });
  });

  return Array.from(merged.values());
}

/**
 * Computes a revision string for a set of node type documents.
 * 
 * The revision changes if any relevant fields change in any document.
 * 
 * @param {Array} docs - Array of node type documents.
 * 
 * @returns {string} - The computed revision string.
 */
function computeRev(docs) {
  const normalizeStamp = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value?.toMillis === 'function') return String(value.toMillis());
    if (typeof value?.seconds === 'number') return `${value.seconds}:${value.nanoseconds ?? 0}`;
    if (typeof value?.__timestamp === 'string') return value.__timestamp;
    return '';
  };

  // Cheap checksum based on keys + key fields that affect UI.
  // This MUST change when relevant type metadata changes, otherwise
  // the cache will incorrectly ignore Firestore updates.
  const base = docs
    .map((d) => {
      const key = normalizeTypeKey(d?.key);
      const version = d?.version ?? 1;
      const dateModified = normalizeStamp(d?.dateModified);
      const showInSelector = typeof d?.showInSelector === 'boolean' ? String(d.showInSelector) : '';
      const label = typeof d?.label === 'string' ? d.label : '';
      const labelPlural = typeof d?.labelPlural === 'string' ? d.labelPlural : '';
      const description = typeof d?.description === 'string' ? d.description : '';
      const iconKey = typeof d?.iconKey === 'string' ? d.iconKey : '';
      const missionTemplates = Array.isArray(d?.missions?.templates) ? d.missions.templates : [];
      const rollups = Array.isArray(d?.rollups) ? d.rollups : [];
      const schemaFields = Array.isArray(d?.schema?.fields) ? d.schema.fields : [];
      const missionsStamp = JSON.stringify(missionTemplates);
      const rollupStamp = JSON.stringify(rollups);
      const schemaStamp = JSON.stringify(schemaFields);
      return `${key}:${version}:${dateModified}:${showInSelector}:${label}:${labelPlural}:${description}:${iconKey}:${missionsStamp}:${rollupStamp}:${schemaStamp}`;
    })
    .sort()
    .join('|');
  // Simple 32-bit hash
  let h = 0;
  for (let i = 0; i < base.length; i++) {
    h = (h * 31 + base.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
}

/**
 * Builds the index structures from raw node type documents.
 * 
 * @param {Array} docs - Array of node type documents.
 * 
 * @returns {Object} - The index object with all, byKey, and aliasToKey maps.
 */
function buildIndex(docs) {
  const byKey = new Map();
  const aliasToKey = new Map();

  // Populate byKey
  docs.forEach((raw) => {
    const obj = { ...raw };
    obj.key = normalizeTypeKey(obj.key || '');
    obj.aliasesUpper = safeArray(obj.aliasesUpper).map(upper);
    obj.aliases = safeArray(obj.aliases);
    if (obj.key) byKey.set(obj.key, obj);
  });

  // Annotate each with primitiveKey and populate aliasToKey
  byKey.forEach((obj) => {
    obj.primitiveKey = obj.key;
  });

  // Populate aliasToKey
  byKey.forEach((obj) => {
    obj.aliasesUpper.forEach((a) => aliasToKey.set(a, obj.key));
  });

  const all = Array.from(byKey.values()).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

  return { all, byKey, aliasToKey };
}

/**
 * Fetches all node type documents for the given scope.
 * 
 * @param {object} db - Firestore database instance.
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {Promise<Array>} - Array of node type documents.
 */
async function fetchAll(db, scopeKey) {
  const scopes = splitScopeKeys(scopeKey).filter((s) => s !== SCOPE_NONE);
  if (!scopes.length) return [];

  const docsByScope = new Map();

  await Promise.all(
    scopes.map(async (scope) => {
      const col = scopeToDatabase(db, scope);
      if (!col) {
        docsByScope.set(scope, []);
        return;
      }
      const q = query(col, orderBy('key'));
      const snap = await getDocs(q);
      const docs = [];
      snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
      docsByScope.set(scope, docs);
    })
  );

  return mergeDocsByScope(scopes, docsByScope);
}

/**
 * Sets up a realtime subscription to all node type documents for the given scope.
 * 
 * @param {object} db - Firestore database instance.
 * @param {string|null} scopeKey - The input scope key.
 * @param {function} onChange - Callback invoked with array of node type documents on change.
 * 
 * @returns {function} - Unsubscribe function.
 */
function subscribeAll(db, scopeKey, onChange) {
  const scopes = splitScopeKeys(scopeKey).filter((s) => s !== SCOPE_NONE);
  if (!scopes.length) return () => {};

  const docsByScope = new Map();

  // Track which scopes have delivered their first snapshot. Only call onChange
  // once ALL scopes have reported at least once — this prevents the UI from
  // rendering with a partial merged set (e.g. only workspace types while the
  // root-scope snapshot is still in-flight), which would cause raw Firestore
  // IDs to appear as type labels.
  const initializedScopes = new Set();

  const updateMerged = () => {
    if (initializedScopes.size < scopes.length) return;
    const merged = mergeDocsByScope(scopes, docsByScope);
    console.debug('[NodeTypesCache] merged update', {
      scopeKey,
      count: merged.length,
    });
    onChange(merged);
  };

  const unsubs = scopes.map((scope) => {
    const col = scopeToDatabase(db, scope);
    if (!col) {
      // Null-collection scope contributes no docs; mark it ready immediately.
      initializedScopes.add(scope);
      return () => {};
    }
    const q = query(col, orderBy('key'));
    return onSnapshot(q, (snap) => {
      const docs = [];
      snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
      docsByScope.set(scope, docs);
      initializedScopes.add(scope);
      console.debug('[NodeTypesCache] scope snapshot', {
        scope,
        count: docs.length,
      });
      updateMerged();
    });
  });

  return () => {
    unsubs.forEach((unsub) => {
      if (typeof unsub === 'function') unsub();
    });
  };
}

/**
 * Reads cached node type documents from localStorage if fresh.
 * 
 * @param {number} ttlMs - Time-to-live in milliseconds.
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {Object|null} - Object with docs array and meta, or null if not found/fresh.
 */
function readFromLS(ttlMs, scopeKey) {
  try {
    const metaRaw = localStorage.getItem(lsKeyMeta(scopeKey));
    const dataRaw = localStorage.getItem(lsKeyData(scopeKey));
    if (!metaRaw || !dataRaw) return null;

    const meta = JSON.parse(metaRaw);
    if (!meta.ts || now() - meta.ts > ttlMs) return null;
    const docs = JSON.parse(dataRaw);
    if (!Array.isArray(docs)) return null;

    return { docs, meta };
  } catch {
    return null;
  }
}

/**
 * Writes node type documents to localStorage.
 * 
 * @param {Array} docs - Array of node type documents.
 * @param {string} rev - Revision string.
 * @param {string|null} scopeKey - The input scope key.
 */
function writeToLS(docs, rev, scopeKey) {
  try {
    localStorage.setItem(lsKeyData(scopeKey), JSON.stringify(docs));
    localStorage.setItem(lsKeyMeta(scopeKey), JSON.stringify({ ts: now(), rev }));
  } catch {
    // ignore quota errors
  }
}

/**
 * NodeTypesCache
 * 
 * In-memory cache of node types for a specific scope.
 */
class NodeTypesCache {
  constructor(scopeKey) {
    this.scopeKey = normalizeScopeKey(scopeKey);
    this.ready = false;
    this.docs = [];
    this.index = buildIndex([]);
    this.rev = '';
    this._unsub = null;
    this._warmToken = 0;
    this._emitCount = 0;
    this._listeners = new Set();
  }

  _emit() {
    this._emitCount++;
    for (const cb of this._listeners) {
      try { cb(this); } catch {}
    }
  }

  onChange(cb) {
    this._listeners.add(cb);
    return () => this._listeners.delete(cb);
  }

  // Public API
  get(key) {
    return this.index.byKey.get(normalizeTypeKey(key));
  }

  getByAlias(alias) {
    const k = this.index.aliasToKey.get(upper(alias));
    return k ? this.index.byKey.get(k) : undefined;
  }

  getAll() {
    return this.index.all;
  }

  isReady() {
    return this.ready;
  }

  destroy() {
    if (this._unsub) {
      this._unsub();
      this._unsub = null;
    }
    this._listeners.clear();
  }
}

/**
 * Map of scopeKey -> NodeTypesCache instances.
 */
const _caches = new Map();
let _activeScopeKey = SCOPE_ROOT;

/**
 * Gets or creates a NodeTypesCache for the given scope.
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {NodeTypesCache} - The NodeTypesCache instance for the given scope.
 */
function getOrCreateCache(scopeKey) {
  const s = normalizeScopeKey(scopeKey);
  const existing = _caches.get(s);
  if (existing) return existing;
  const created = new NodeTypesCache(s);
  _caches.set(s, created);

  // Synchronously pre-warm from localStorage so the very first render has real data.
  // The async warmNodeTypesCache call will refresh/subscribe afterward.
  const ls = readFromLS(DEFAULT_TTL_MS, s);
  if (ls) {
    created.docs = ls.docs;
    created.index = buildIndex(created.docs);
    created.rev = ls.meta.rev || computeRev(created.docs);
    created.ready = true;
    // No _emit() here — listeners haven't subscribed yet (this is synchronous, pre-mount).
  }

  return created;
}

/**
 * Sets the active scope for the singleton cache.
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {NodeTypesCache} - The NodeTypesCache instance for the active scope.
 */
export function setActiveNodeTypesScope(scopeKey) {
  _activeScopeKey = normalizeScopeKey(scopeKey);
  return getOrCreateCache(_activeScopeKey);
}

/**
 * Access a specific scope's NodeTypesCache.
 * 
 * @param {string|null} scopeKey - The input scope key.
 * 
 * @returns {NodeTypesCache} - The NodeTypesCache instance for the given scope.
 */
export function getNodeTypesCacheForScope(scopeKey) {
  return getOrCreateCache(scopeKey);
}

/**
 * Warm the cache.
 * - Uses localStorage snapshot if fresh.
 * - Fetches from Firestore.
 * - Optional realtime subscription.
 * 
 * @param {object} db - Firestore database instance.
 * @param {Object} options - Options object.
 * @param {string|null} [options.scopeKey] - The input scope key.
 * @param {boolean} [options.subscribe=false] - Whether to setup realtime subscription.
 * @param {number} [options.ttlMs=DEFAULT_TTL_MS] - TTL for localStorage cache in milliseconds.
 * 
 * @returns {Promise<NodeTypesCache>} - The warmed NodeTypesCache instance.
 */
export async function warmNodeTypesCache(
  db,
  { scopeKey = SCOPE_ROOT, subscribe = false, ttlMs = DEFAULT_TTL_MS } = {}
) {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  const cache = getOrCreateCache(normalizedScopeKey);
  const warmToken = cache._warmToken + 1;
  cache._warmToken = warmToken;

  // Explicitly do nothing for NONE: callers want an empty cache and no Firestore reads.
  if (normalizedScopeKey === SCOPE_NONE) {
    if (!cache.ready) {
      cache.docs = [];
      cache.index = buildIndex([]);
      cache.rev = '';
      cache.ready = true;
      cache._emit();
    }
    return cache;
  }

  // Try LS first for instant reads
  const ls = readFromLS(ttlMs, normalizedScopeKey);
  if (ls && !cache.ready) {
    cache.docs = ls.docs;
    cache.index = buildIndex(cache.docs);
    cache.rev = ls.meta.rev || computeRev(cache.docs);
    cache.ready = true;
    cache._emit();
  }

  // Start realtime subscription before fetch so live updates are not blocked by slower refresh calls.
  if (subscribe && !cache._unsub) {
    cache._unsub = subscribeAll(db, normalizedScopeKey, (liveDocs) => {
      const liveRev = computeRev(liveDocs);
      cache.docs = liveDocs;
      cache.index = buildIndex(liveDocs);
      cache.rev = liveRev;
      cache.ready = true;
      writeToLS(liveDocs, liveRev, normalizedScopeKey);
      cache._emit();
    });
  }

  // Fetch fresh from server
  const docs = await fetchAll(db, normalizedScopeKey);
  if (cache._warmToken !== warmToken) {
    return cache;
  }

  const rev = computeRev(docs);
  cache.docs = docs;
  cache.index = buildIndex(docs);
  cache.rev = rev;
  cache.ready = true;
  writeToLS(docs, rev, normalizedScopeKey);
  cache._emit();

  return cache;
}

function patchNodeTypeSchemaInCache(cache, { typeKey, fields } = {}) {
  if (!cache || !cache.ready) return false;

  const normalizedTypeKey = normalizeTypeKey(typeKey);
  if (!normalizedTypeKey) return false;

  const nextSchemaFields = Array.isArray(fields)
    ? fields.map((field) => ({ ...(field || {}) }))
    : [];

  let changed = false;
  const nextDocs = (Array.isArray(cache.docs) ? cache.docs : []).map((docData) => {
    const docTypeKey = normalizeTypeKey(docData?.key);
    if (docTypeKey !== normalizedTypeKey) return docData;

    changed = true;
    return {
      ...(docData || {}),
      schema: {
        ...(docData?.schema || {}),
        fields: nextSchemaFields,
      },
    };
  });

  if (!changed) return false;

  const nextRev = computeRev(nextDocs);
  cache.docs = nextDocs;
  cache.index = buildIndex(nextDocs);
  cache.rev = nextRev;
  cache.ready = true;
  writeToLS(nextDocs, nextRev, cache.scopeKey);
  cache._emit();

  return true;
}

function cloneNodeTypeMissions(missions) {
  if (!missions || typeof missions !== 'object' || Array.isArray(missions)) {
    return { templates: [] };
  }

  const templates = Array.isArray(missions.templates)
    ? missions.templates.map((template) => ({
      ...(template || {}),
      tasks: Array.isArray(template?.tasks)
        ? template.tasks.map((task) => ({ ...(task || {}) }))
        : [],
    }))
    : [];

  return {
    ...missions,
    templates,
  };
}

function patchNodeTypeMissionsInCache(cache, { typeKey, missions } = {}) {
  if (!cache || !cache.ready) return false;

  const normalizedTypeKey = normalizeTypeKey(typeKey);
  if (!normalizedTypeKey) return false;

  const nextMissions = cloneNodeTypeMissions(missions);

  let changed = false;
  const nextDocs = (Array.isArray(cache.docs) ? cache.docs : []).map((docData) => {
    const docTypeKey = normalizeTypeKey(docData?.key);
    if (docTypeKey !== normalizedTypeKey) return docData;

    changed = true;
    return {
      ...(docData || {}),
      missions: nextMissions,
    };
  });

  if (!changed) return false;

  const nextRev = computeRev(nextDocs);
  cache.docs = nextDocs;
  cache.index = buildIndex(nextDocs);
  cache.rev = nextRev;
  cache.ready = true;
  writeToLS(nextDocs, nextRev, cache.scopeKey);
  cache._emit();

  return true;
}

/**
 * Applies an optimistic schema field patch to warmed workspace node type caches.
 *
 * This updates in-memory/LS cache immediately so UI consumers can render new
 * schema changes without waiting for a full network refresh roundtrip.
 *
 * @param {Object} options - Options object.
 * @param {string|null} [options.workspaceKey] - Workspace key for workspace-scoped node types.
 * @param {string} options.typeKey - Node type key.
 * @param {Array<object>} options.fields - Next schema fields array.
 *
 * @returns {number} - Number of warmed cache scopes patched.
 */
export function patchNodeTypeSchemaForWorkspace({ workspaceKey = null, typeKey, fields } = {}) {
  const normalizedWorkspaceKey = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';
  const scopeFragment = normalizedWorkspaceKey
    ? makeWorkspaceScopeKeyStrict(normalizedWorkspaceKey)
    : SCOPE_ROOT;

  let patchedCount = 0;
  _caches.forEach((cache) => {
    if (!normalizedWorkspaceKey && cache.scopeKey !== SCOPE_ROOT) return;

    const scopeParts = splitScopeKeys(cache.scopeKey);
    if (!scopeParts.includes(scopeFragment)) return;
    if (patchNodeTypeSchemaInCache(cache, { typeKey, fields })) {
      patchedCount += 1;
    }
  });

  return patchedCount;
}

export function patchNodeTypeMissionsForWorkspace({ workspaceKey = null, typeKey, missions } = {}) {
  const normalizedWorkspaceKey = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';
  const scopeFragment = normalizedWorkspaceKey
    ? makeWorkspaceScopeKeyStrict(normalizedWorkspaceKey)
    : SCOPE_ROOT;

  let patchedCount = 0;
  _caches.forEach((cache) => {
    if (!normalizedWorkspaceKey && cache.scopeKey !== SCOPE_ROOT) return;

    const scopeParts = splitScopeKeys(cache.scopeKey);
    if (!scopeParts.includes(scopeFragment)) return;
    if (patchNodeTypeMissionsInCache(cache, { typeKey, missions })) {
      patchedCount += 1;
    }
  });

  return patchedCount;
}

/**
 * Syncs schema updates into cache immediately, then performs a network refresh.
 *
 * @param {object} db - Firestore database instance.
 * @param {Object} options - Options object.
 * @param {string|null} [options.workspaceKey] - Workspace key for workspace-scoped node types.
 * @param {string} [options.typeKey] - Node type key.
 * @param {Array<object>} [options.fields] - Next schema fields array.
 * @param {boolean} [options.subscribe=false] - Whether to setup realtime subscription.
 * @param {number} [options.ttlMs=0] - TTL for localStorage cache in milliseconds.
 *
 * @returns {Promise<NodeTypesCache>} - The warmed NodeTypesCache instance.
 */
export async function syncNodeTypeSchemaCacheForWorkspace(
  db,
  {
    workspaceKey = null,
    typeKey = '',
    fields = null,
    subscribe = false,
    ttlMs = 0,
  } = {}
) {
  if (typeKey && Array.isArray(fields)) {
    patchNodeTypeSchemaForWorkspace({ workspaceKey, typeKey, fields });
  }

  return warmNodeTypesCacheForWorkspace(db, {
    workspaceKey,
    subscribe,
    ttlMs,
  });
}

/**
 * Warm node types cache for a workspace scope merged with ROOT fallback.
 *
 * @param {object} db - Firestore database instance.
 * @param {Object} options - Options object.
 * @param {string|null} [options.workspaceKey] - Workspace key for workspace-scoped node types.
 * @param {boolean} [options.subscribe=false] - Whether to setup realtime subscription.
 * @param {number} [options.ttlMs=DEFAULT_TTL_MS] - TTL for localStorage cache in milliseconds.
 *
 * @returns {Promise<NodeTypesCache>} - The warmed NodeTypesCache instance.
 */
export async function warmNodeTypesCacheForWorkspace(
  db,
  { workspaceKey = null, subscribe = false, ttlMs = DEFAULT_TTL_MS } = {}
) {
  const scopeKey = workspaceKey
    ? makeCompositeScopeKey([
      makeWorkspaceScopeKeyStrict(workspaceKey),
      SCOPE_ROOT,
    ])
    : SCOPE_ROOT;

  return warmNodeTypesCache(db, { scopeKey, subscribe, ttlMs });
}

/**
 * Access the singleton cache after warming.
 * Throws if you forgot to call warmNodeTypesCache first.
 * 
 * @returns {NodeTypesCache} - The NodeTypesCache instance for the active scope.
 */
export function getNodeTypesCache() {
  const cache = getOrCreateCache(_activeScopeKey);
  if (!cache.ready) {
    // Still return; but warn—callers can check isReady()
    // throw new Error('NodeTypes cache not warmed. Call warmNodeTypesCache(db) first.');
  }
  return cache;
}

/**
 * Lookup a node type definition by key from the active cache.
 * 
 * @param {string} typeKey - The node type key.
 * 
 * @returns {Object|undefined} - The node type definition, or undefined if not found.
 */
export function getNodeType(typeKey) {
  if (!typeKey) return undefined;
  return getNodeTypesCache().get(typeKey);
}

export function makeWorkspaceScopeKey(workspaceKey) {
  const wk = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';
  if (!wk) return SCOPE_NONE;
  return `${SCOPE_WORKSPACE_PREFIX}${wk}`;
}

export function makeWorkspaceScopeKeyStrict(workspaceKey) {
  return makeWorkspaceScopeKey(workspaceKey);
}

export function makeCompositeScopeKey(scopeKeys = []) {
  const normalized = (scopeKeys || [])
    .map((s) => normalizeScopeKey(s))
    .filter((s) => s && s !== SCOPE_NONE);

  if (!normalized.length) return SCOPE_NONE;

  const deduped = [];
  normalized.forEach((s) => {
    if (!deduped.includes(s)) deduped.push(s);
  });

  return deduped.join('|');
}

/**
 * Returns the NONE scope key.
 * 
 * @returns {string} - The NONE scope key.
 */
export function getNoneScopeKey() {
  return SCOPE_NONE;
}
